// PRC-035/036: plantilla «Ajustes» (bocetos G1, G3, G4): índice a la izquierda y
// bloques con título, explicación y acción. Mi perfil, Mi suscripción, Usuarios y Planes.
import React, { useEffect } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { J } from '../journeyTokens';
import PageHeader, { pageFrameSx } from './PageHeader';
import { MENU_NAVIGATION_STATE } from './PageTransition';

type IndexItem = { label: string; to: string; adminOnly?: boolean };

const INDEX: IndexItem[] = [
  { label: 'Mi perfil', to: '/profile' },
  { label: 'Mi suscripción', to: '/subscription', adminOnly: true },
  { label: 'Usuarios del equipo', to: '/team', adminOnly: true },
  { label: 'Planes', to: '/planes', adminOnly: true },
];

export interface SettingsFrameProps {
  title: string;
  // Dato de contexto junto al título («Laura Martín · Administradora»).
  context?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const SettingsFrame: React.FC<SettingsFrameProps> = ({ title, context, actions, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPricingAdmin } = useAuth();
  const current = `${location.pathname}${location.hash}`;
  const isActive = (to: string) => (to.includes('#') ? current === to : location.pathname === to && (!location.hash || to !== '/profile'));

  const go = (to: string) => {
    const [path, hash] = to.split('#');
    if (path === location.pathname) {
      // Misma pantalla: se cambia el ancla (o se quita) y el efecto de abajo hace el scroll.
      navigate(hash ? `#${hash}` : location.pathname, { replace: true });
      if (!hash) window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(to, { state: MENU_NAVIGATION_STATE });
  };

  // PRC-047: «Seguridad» ya no está en el índice; su dirección antigua lleva a Mi perfil.
  useEffect(() => {
    if (location.pathname === '/profile' && location.hash === '#seguridad') navigate('/profile', { replace: true, state: location.state });
  }, [location.pathname, location.hash, navigate]);

  // Un ancla en la dirección baja a su sección.
  useEffect(() => {
    if (!location.hash || (location.pathname === '/profile' && location.hash === '#seguridad')) return undefined;
    const timer = window.setTimeout(() => document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 260);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  return (
    <Box sx={pageFrameSx}>
      <PageHeader eyebrow="Mi cuenta" title={title} description={context} actions={actions} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr)' }, gap: { xs: 2, md: 4 }, alignItems: 'start' }}>
        <Box component="nav" aria-label="Tu cuenta" sx={{ position: { md: 'sticky' }, top: { md: 24 }, display: 'flex', flexDirection: 'column', gap: '2px', fontFamily: J.font }}>
          <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: J.muted, p: '4px 10px 8px' }}>Tu cuenta</Box>
          {INDEX.filter((item) => !item.adminOnly || isPricingAdmin).map((item) => {
            const active = isActive(item.to);
            return (
              <ButtonBase
                key={item.to}
                onClick={() => go(item.to)}
                aria-current={active ? 'page' : undefined}
                sx={{ justifyContent: 'flex-start', minHeight: 36, px: '10px', borderRadius: '8px', fontFamily: J.font, fontSize: 14, fontWeight: 600, color: active ? '#e0f2fe' : J.body, bgcolor: active ? 'rgba(56,189,248,.14)' : 'transparent', '&:hover': { bgcolor: active ? 'rgba(56,189,248,.18)' : 'rgba(148,163,184,.08)' } }}
              >
                {item.label}
              </ButtonBase>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0, maxWidth: 960 }}>{children}</Box>
      </Box>
    </Box>
  );
};

// Bloque de ajustes: título, explicación y acción a la derecha; contenido debajo.
export const SettingsBlock: React.FC<{
  id?: string;
  title: string;
  text?: React.ReactNode;
  aside?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ id, title, text, aside, children, footer }) => (
  <Box id={id} component="section" sx={{ scrollMarginTop: 24, border: `1px solid ${J.border}`, borderRadius: J.radiusCard, bgcolor: J.card, fontFamily: J.font }}>
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box component="h2" sx={{ m: 0, fontSize: 17, fontWeight: 800, color: J.text }}>{title}</Box>
          {text && <Box component="p" sx={{ m: 0, mt: '4px', fontSize: 13.5, color: J.muted, lineHeight: 1.5 }}>{text}</Box>}
        </Box>
        {aside}
      </Box>
      {children}
    </Box>
    {footer && (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75, borderTop: `1px solid ${J.border}`, display: 'flex', justifyContent: 'flex-end', gap: 1.25, flexWrap: 'wrap' }}>
        {footer}
      </Box>
    )}
  </Box>
);

export default SettingsFrame;
